import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../app/store";
import { TBS_API_URL } from "../../config";
import type {
  Item,
  ItemCard,
  ItemInput,
  ItemList,
  ItemListParams,
  ItemPos,
  ItemStock,
  ItemUpdateInput,
} from "../../types/tbs/items";

// Tiendas Biai — items and barcodes.
//
// **Not `src/services/items/`**, which is the storefront's DynamoDB catalog. Two resources, one
// word; this is the commercial catalog behind the till.

const buildQueryString = (params: Record<string, string | number | boolean | undefined>) => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === "" || value === false) continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

export const tbsItemsApi = createApi({
  reducerPath: "tbsItemsApi",
  tagTypes: ["TbsItem", "TbsItemStock"],
  baseQuery: fetchBaseQuery({
    baseUrl: TBS_API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", "application/json");
      // Only when one is selected: with a single membership the authorizer picks the company
      // itself, and a wrong value is a refusal rather than a fallback. Runtime since APP-1.
      const tenantId = (getState() as RootState).tbsTenant.tenantId;
      if (tenantId) headers.set("X-Tenant-Id", tenantId);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    /** The admin list. `view` defaults to `card` server-side; a screen needing full rows says so. */
    getItems: builder.query<ItemList<Item | ItemCard>, ItemListParams | void>({
      query: (params) => `/v1/items${buildQueryString({ ...(params ?? {}) })}`,
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((i) => ({ type: "TbsItem" as const, id: i.id })),
              { type: "TbsItem" as const, id: "LIST" },
            ]
          : [{ type: "TbsItem" as const, id: "LIST" }],
    }),

    getItem: builder.query<Item, string>({
      query: (id) => `/v1/items/${id}`,
      providesTags: (_r, _e, id) => [{ type: "TbsItem", id }],
    }),

    /**
     * **The scan.** One indexed lookup server-side, and a person is waiting on it.
     *
     * `keepUnusedDataFor: 0` — a scan must never be answered from cache. Price and stock change
     * while the till is open, and a cached line would sell yesterday's price without anyone
     * noticing. The POS's own IndexedDB cache is a different thing, deliberately: it is a fallback
     * for a dropped connection, not a way to skip a round trip that is working.
     */
    getItemByBarcode: builder.query<ItemPos, { code: string; warehouseId?: string }>({
      query: ({ code, warehouseId }) =>
        `/v1/items/by-barcode/${encodeURIComponent(code)}${buildQueryString({ warehouse_id: warehouseId })}`,
      keepUnusedDataFor: 0,
    }),

    /** Bulk, for the POS grid — one request per line on screen is what this exists to avoid. */
    getItemStock: builder.query<{ data: ItemStock[] }, { itemIds: string[]; warehouseId?: string }>(
      {
        query: ({ itemIds, warehouseId }) =>
          `/v1/item-stock${buildQueryString({ item_id: itemIds.join(","), warehouse_id: warehouseId })}`,
        providesTags: [{ type: "TbsItemStock", id: "LIST" }],
      },
    ),

    createItem: builder.mutation<Item, ItemInput>({
      query: (body) => ({ url: "/v1/items", method: "POST", body }),
      invalidatesTags: [{ type: "TbsItem", id: "LIST" }],
    }),

    /** `If-Match` is required, and the version must come from the row the form was loaded with. */
    updateItem: builder.mutation<
      Item,
      { id: string; version: number; body: Partial<ItemUpdateInput> }
    >({
      query: ({ id, version, body }) => ({
        url: `/v1/items/${id}`,
        method: "PUT",
        headers: { "If-Match": String(version) },
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "TbsItem", id },
        { type: "TbsItem", id: "LIST" },
      ],
    }),

    deleteItem: builder.mutation<void, string>({
      query: (id) => ({ url: `/v1/items/${id}`, method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "TbsItem", id },
        { type: "TbsItem", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetItemsQuery,
  useGetItemQuery,
  useGetItemByBarcodeQuery,
  useLazyGetItemByBarcodeQuery,
  useGetItemStockQuery,
  useCreateItemMutation,
  useUpdateItemMutation,
  useDeleteItemMutation,
} = tbsItemsApi;
